import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useRef, useState } from "react"
import { FaPlus } from "react-icons/fa"

import { useCreateHousehold } from "@/hooks/api/useHouseholds"
import { usePermissions } from "@/hooks/usePermissions"
import { householdUpdateSchema } from "@/types/household"
import type { AddHouseholdProps, HouseholdCreateInput, HouseholdUpdateInput } from "@/types/household"
import { Button } from "@chakra-ui/react"

import HouseholdFormDialog from "./HouseholdFormDialog"

const AddHousehold = memo(({ onSuccess, onCancel }: AddHouseholdProps = {}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { hasPermission } = usePermissions()
  const createHouseholdMutation = useCreateHousehold()
  const addButtonRef = useRef<HTMLButtonElement | null>(null)

  if (!hasPermission("households:create")) {
    return null
  }

  const defaultValues = useMemo<HouseholdUpdateInput>(
    () => ({
      user_id: "",
      parent_first_name: "",
      parent_last_name: "",
      email: "",
      phone: "",
      secondary_phone: "",
      address: "",
      emergency_contact_name: "",
      emergency_contact_phone: "",
      emergency_contact_relationship: "",
      doctor_first_name: "",
      doctor_last_name: "",
      doctor_phone: "",
      insurance_provider: "",
      referral_source: "",
      agreed_to_policy: false,
      agreed_to_hold_harmless: false,
      policy_agreement_date: "",
      billing_email: "",
      auto_pay_enabled: false,
      notes: "",
      is_active: true,
    }),
    [],
  )

  const handleClose = () => {
    setIsOpen(false)
    onCancel?.()
  }

  const handleSubmit = async (data: HouseholdUpdateInput) => {
    // Cast to CreateInput since all required fields will be present after form validation
    await createHouseholdMutation.mutateAsync(data as HouseholdCreateInput)
    onSuccess?.()
  }

  return (
    <>
      <Button
        ref={addButtonRef}
        onClick={() => setIsOpen(true)}
        colorPalette="blue"
        size="sm"
        aria-label="Add new household"
      >
        <FaPlus fontSize="14px" aria-hidden="true" />
        Add Family
      </Button>

      <HouseholdFormDialog
        isOpen={isOpen}
        title="Add New Family"
        submitLabel="Create Family"
        resolver={zodResolver(householdUpdateSchema)}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onClose={handleClose}
        isSubmitting={createHouseholdMutation.isPending}
        finalFocusRef={addButtonRef}
        showSkipLink
        loadingText="Creating..."
      />
    </>
  )
})

AddHousehold.displayName = "AddHousehold"

export default AddHousehold
export type { AddHouseholdProps }
